/* eslint-disable camelcase */
import { useParams } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { useSnapshot } from 'valtio';

import * as Pioneer from 'pioneer';

import { eventsStore, gmapStore, videoStore } from '../managers/globalState';
import GeologyMapLegend from '../components/geology_map_legend';
import globalRefs from '../managers/globalRefs';
import { loadGmap } from '../helpers/processGmap';
import { loadGeoLocatedStatic, cleanupAnimation } from '../helpers/processEvents';
import { getProjectGmapByParam } from '../helpers/processProjects';
import { hideLoading, showLoading } from '../components/loading';

const GmapDetail = () => {
	const { gmapParam } = useParams();
	const { gmaps } = useSnapshot(gmapStore.state);
	const { events } = useSnapshot(eventsStore.state);
	const isLoading = useRef(null);
	const currentGmap = getProjectGmapByParam({ gmaps, events, gmapParam });
	// console.log("GmapDetail: loading", gmapParam, currentGmap)

	// useEffect for when event changes.
	useEffect(() => {
		// Set variables.
		const { pioneer, getManager } = globalRefs;
		const { external_id, type, isAnimated } = currentGmap || {};
		// console.log("GmapDetail: use effect", external_id)

		const alreadyLoading = isLoading.current === external_id;

		if (!currentGmap || alreadyLoading) {
			return;
		}

		// Set isLoading.
		isLoading.current = external_id;

		// Show loading.
		const loadType = isAnimated ? 'video' : 'event';
		showLoading(loadType);

		// Set current event in global state.
		gmapStore.setGlobalState({ currentGmap });
		if (isAnimated) {
			videoStore.setGlobalState({ isVideoTextureLoaded: true });
		}



		const patchComponent = pioneer.getScene('main').getEntity('earth').getComponent(Pioneer.SpheroidLODComponent, 'patch');

	
		const { images, animation_speed } = currentGmap;
		const hasFrames = Array.isArray(images) && images.length > 0
			&& typeof animation_speed === 'number' && animation_speed > 0;

		// Load the video if it is animated or static if not.
		(isAnimated
			? getManager('video').loadVideo(currentGmap)
			: hasFrames ? loadGeoLocatedStatic(currentGmap) : loadGmap(currentGmap))
		// NOTE: Currently assuming global static events are not possible.
			.then(() => {
				// console.log('loaded', external_id);
				// Check if event is still loading.
				if (isLoading.current) {
					// Hide loading.
					hideLoading(loadType);
				}

				// Set isLoading to false.
				isLoading.current = false;
			}).catch(e => e);

		// On currentVideo change / unmount disable the earth patch.
		return () => {
			// Stop any frame animation before switching gmaps.
			cleanupAnimation();
			// Clear the video flags (isVideoTextureLoaded / isVideoPlaying) if this was a video gmap.
			if (isAnimated) {
				getManager('video').unloadVideo();
			}
			if (type === 'geoLocated') {
				patchComponent?.setEnabled?.(false);
			}
		};
	}, [currentGmap?.external_id]);

	// useEffect to set current event to null and disable patch when leaving event detail view.
	// eslint-disable-next-line arrow-body-style
	useEffect(() => {
		return () => {
			const { pioneer, getManager } = globalRefs;
			const patchComponent = pioneer.getScene('main').getEntity('earth').getComponent(Pioneer.SpheroidLODComponent, 'patch');

			// Set isLoading to null and hide loading.
			isLoading.current = null;
			hideLoading('event');
			hideLoading('video');

			cleanupAnimation();

			// Disable the patch.
			patchComponent.setEnabled(false);

			// Reset global states.
			gmapStore.setGlobalState({ currentGmap: null });

			
			getManager('video').unloadVideo();
		};
	}, []);
	return <GeologyMapLegend current={currentGmap} />;
};

export default GmapDetail;
